(function () {
    'use strict';

    angular
        .module('atheer')
        .factory('idleHandler', idleHandler);

    /* @ngInject */
    function idleHandler($rootScope, $window, $document, $interval, Idle, Setting, $mdDialog, $location, Keepalive, Auth, $state, $http, SessionLastActive) {
        var isWarning = false;
        var idleTimeoutMs = null;
        var listenersBound = false;
        var lastActivityWrittenAt = 0;
        var ACTIVITY_WRITE_THROTTLE_MS = 2000;
        /*
         * Margin used when checking persisted last-active-time at IdleStart.
         * ng-idle fires IdleStart exactly when its internal counter reaches
         * the idle limit, so by design last-active-time will be ~idleTimeoutMs
         * old at that point. Only treat it as "tab was suspended past the
         * limit" when it's STALE_GRACE_MS older than that, otherwise the
         * normal-flow IdleStart would short-circuit straight into a logout
         * and the warning dialog would never appear.
         */
        var STALE_GRACE_MS = 5000;
        /*
         * How often to refresh last-active-time while a long-running
         * foreground activity (airsession, job, bulk upload, API call,
         * copilot) is in progress but the user isn't generating DOM
         * activity events. Without this the persisted timestamp would
         * legitimately go stale and any cross-tab/cross-app listener
         * (e.g. another fos-web tab or ibm-frontend on the same origin)
         * would treat the user as idle and sign them out mid-activity.
         */
        var IN_PROGRESS_HEARTBEAT_MS = 15000;

        return {
            initialize: initialize
        };

        function initialize() {
            loadSettingsAndStartWatching(/* isInitialLoad */ true);
            startInProgressHeartbeat();

            /*
             * After a successful sign-in we have to restart Idle.watch().
             * signOutNow() calls Idle.unwatch() and the .run() block that
             * bootstrapped this handler fires only once per app load, so
             * without this listener a same-tab sign-out → sign-in cycle
             * (the exact path when the user reopens the tab past the
             * wait time and then signs back in) would leave the watcher
             * stopped forever and the user would never auto-logout.
             *
             * Re-read settings so the freshly signed-in session also
             * picks up any workspace timeout change that happened while
             * we were idle.
             */
            $rootScope.$on('authenticationSuccess', function () {
                loadSettingsAndStartWatching(/* isInitialLoad */ false);
            });

            $rootScope.$on('IdleStart', function () {
                // Catch "tab was suspended past the limit" cases (e.g. laptop
                // wake, long-hidden background tab). Apply STALE_GRACE_MS so
                // the normal-flow IdleStart (where now - last ≈ idleTimeoutMs
                // by design) doesn't short-circuit straight to logout.
                if (enforcePersistedIdle(STALE_GRACE_MS)) {
                    return;
                }

                if (Auth.isPublicUser() || isOnAuthRoute() || isInProgressActivity()) {
                    Keepalive.setInterval(15);
                    return;
                }

                if (isWarning) {
                    $mdDialog.show({
                        templateUrl: 'core/components/session-timeout/session-timeout-dialog.tmpl.html',
                        clickOutsideToClose: true,
                        controller: "SessionTimeoutController",
                        controllerAs: "vm"
                    }).then(angular.noop, angular.noop);
                } else {
                    signOutNow();
                }
            });

            $rootScope.$on('IdleEnd', function () {
                writeOwnActivity();
            });

            $rootScope.$on('IdleTimeout', function () {
                Keepalive.setInterval(15);
            });
        }

        /*
         * In-progress activity flags set by long-running foreground work
         * (airsession video, job execution, in-flight API calls, bulk
         * upload, copilot). These activities frequently do not emit
         * DOM-level events (mousedown/keydown/scroll/touchstart) so the
         * idle counter and the persisted timestamp would otherwise treat
         * the user as idle.
         */
        function isInProgressActivity() {
            return !!($rootScope.isSessionStarted
                || $rootScope.isJobExecutionActive
                || $rootScope.isApiProcessing
                || $rootScope.isBulkUploadInProgress
                || $rootScope.isCopilotApiProcessing);
        }

        function isOnAuthRoute() {
            var path = $location.path();
            return path === '/signin' || path === '/signout' || path.startsWith('/join');
        }

        /*
         * Keeps last-active-time fresh while an in-progress activity is
         * running so neither this tab's enforcePersistedIdle nor any
         * cross-tab/cross-app listener (other fos-web tab, ibm-frontend
         * on the same origin) signs the user out mid-activity. Idle is
         * interrupted as well so the warning dialog doesn't appear
         * behind an active airsession.
         */
        function startInProgressHeartbeat() {
            $interval(function () {
                if (idleTimeoutMs == null) {
                    return;
                }
                if (!isInProgressActivity()) {
                    return;
                }
                Idle.interrupt();
                writeOwnActivity();
            }, IN_PROGRESS_HEARTBEAT_MS);
        }

        /*
         * Shared between the initial .run()-time setup and the
         * post-sign-in restart. isInitialLoad gates the "past limit on
         * boot" sign-out: on a fresh sign-in the timestamp was just
         * written by AuthServerProvider.login() and any stale-check
         * here would be a false positive.
         */
        function loadSettingsAndStartWatching(isInitialLoad) {
            Setting.getAuthenticationSetupSettings(function (result) {
                var fields = (result && result[0] && result[0].fields) ? result[0].fields : null;
                idleTimeoutMs = SessionLastActive.getSessionIdleTimeoutMs(fields);

                if (idleTimeoutMs == null) {
                    return;
                }

                // On initial app load (e.g. Chrome reopen) the timestamp
                // is from a previous session, so any "past limit"
                // reading is real - no grace.
                if (isInitialLoad && enforcePersistedIdle(0)) {
                    return;
                }

                var PROMPT_BEFORE_SECS = 10;
                Idle.setIdle(Math.max(idleTimeoutMs / 1000 - PROMPT_BEFORE_SECS, 1));
                Idle.watch();

                isWarning = SessionLastActive.isWarningEnabled(fields);

                bindListeners();
                writeOwnActivity();
            });
        }

        /**
         * @param {number} [graceMs] Extra milliseconds beyond idleTimeoutMs
         *   before we treat the timestamp as stale.
         * @returns {boolean} true when the user was signed out.
         */
        function enforcePersistedIdle(graceMs) {
            if (idleTimeoutMs == null) {
                return false;
            }
            var lastAt = SessionLastActive.readSessionLastActiveAt();
            if (lastAt == null) {
                writeOwnActivity();
                return false;
            }
            var grace = (typeof graceMs === 'number') ? graceMs : 0;
            if (Date.now() - lastAt < idleTimeoutMs + grace) {
                return false;
            }
            /*
             * Skip the stale-timestamp signout while a long-running
             * foreground activity is in progress. Airsession video and
             * similar flows do not emit DOM activity events, so the
             * persisted timestamp can legitimately appear stale even
             * though the user is engaged. The IN_PROGRESS_HEARTBEAT will
             * refresh the timestamp shortly after this returns.
             *
             * Also skip for public users (no Authorization token in
             * localStorage) — public.* routes don't have a signed-in
             * session, so signOutNow() would needlessly redirect a guest
             * to /signout.
             */
            if (Auth.isPublicUser() || isInProgressActivity()) {
                return false;
            }
            $mdDialog.cancel();
            signOutNow();
            return true;
        }

        function signOutNow() {
            Idle.unwatch();
            Auth.logout();
            $state.go('account.signout');
        }

        function bindListeners() {
            if (listenersBound) {
                return;
            }
            listenersBound = true;

            var events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
            for (var i = 0; i < events.length; i++) {
                $document[0].addEventListener(events[i], onUserActivity, true);
            }

            $document[0].addEventListener('visibilitychange', onVisibilityChange);
            $window.addEventListener('focus', onWindowFocus);
            $window.addEventListener('pageshow', onPageShow);

            /*
             * Cross-tab activity sync: when another tab on the same origin
             * (e.g. ibm-frontend) writes last-active-time, reset our idle
             * counter so we don't fire a stale session-timeout dialog while
             * the user is active in that other tab.
             *
             * The storage event fires only in OTHER tabs - never in the
             * writer - so this listener cannot echo from our own
             * writeOwnActivity() writes. We deliberately do NOT write the
             * timestamp again from this handler; we just trust the value
             * the other tab wrote.
             */
            $window.addEventListener('storage', onStorage);
            $window.addEventListener('message', onNgxIframeActivityMessage);
        }

        function onNgxIframeActivityMessage(event) {
            if (!event || event.data !== Object(event.data) || event.data.atheerIdleActivity !== true) {
                return;
            }
            var hostUrl =
                ($window.location.hostname === 'localhost' || $window.location.hostname === 'demo.atheer.my')
                    ? 'https://' + $window.location.hostname + ':4200'
                    : $window.location.origin;
            if (event.origin !== hostUrl) {
                return;
            }
            if (idleTimeoutMs == null) {
                return;
            }
            Idle.interrupt();
            onUserActivity();
        }

        function onUserActivity() {
            var now = Date.now();
            if (now - lastActivityWrittenAt < ACTIVITY_WRITE_THROTTLE_MS) {
                return;
            }
            writeOwnActivity(now);
        }

        function writeOwnActivity(now) {
            var ts = (typeof now === 'number' && isFinite(now)) ? now : Date.now();
            lastActivityWrittenAt = ts;
            SessionLastActive.writeSessionLastActiveAt(ts);
        }

        function onVisibilityChange() {
            if ($document[0].visibilityState === 'visible') {
                enforcePersistedIdle(STALE_GRACE_MS);
            }
        }

        function onWindowFocus() {
            enforcePersistedIdle(STALE_GRACE_MS);
        }

        function onPageShow(e) {
            if (e && e.persisted) {
                enforcePersistedIdle(STALE_GRACE_MS);
            }
        }

        function onStorage(e) {
            if (!e || e.key !== SessionLastActive.SESSION_LAST_ACTIVE_AT_KEY) {
                return;
            }
            if (e.storageArea && e.storageArea !== $window.localStorage) {
                return;
            }
            if (!e.newValue) {
                return;
            }
            if (idleTimeoutMs == null) {
                return;
            }
            Idle.interrupt();
        }

    }
})();
